/**
 * BelgeVaryantModal.jsx — bir çalışanın SGK / İSG kutusuna belge yüklerken VARYANT seçtirir. Backlog #142.
 *
 *   SGK:  İşe Giriş Bildirgesi (95, tek kişi)   ·  Hizmet Dökümü (96, firmanın belgesi, çok kişi)
 *   İSG:  İSG Sertifikası      (97, tek kişi)   ·  Eğitim Katılım Listesi (98, firmanın belgesi, çok kişi)
 *
 * ⚠️ Seçim BİLEREK iki büyük kart: bir İşe Giriş Bildirgesi'nin on iki kişiye düşmesi (ya da bir Hizmet
 * Dökümü'nün tek kişiye kapanması) tam da #144'ün temizlemek için var olduğu hata. Eski 6 / 7 tipleri
 * yeni yüklemelerde sunulmuyor.
 *
 *   tek        → POST /personnel/dosyaekle/{personId}
 *   paylaşımlı → ya bu çalışan seçili YENİ belge (PaylasimliBelgeYukleModal), ya firmanın MEVCUT bir
 *                belgesine bu çalışanı EKLE (POST .../paylasimli-belgeler/{belgeId}/kisiler).
 * Firma = belgeyi sahiplenen firma; çalışan o firmanın güncel personel listesinde olmalı (değilse 400).
 */
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import PaylasimliBelgeYukleModal from "../Firma/PaylasimliBelgeYukleModal";
import TarihGirdisi from "../Common/TarihGirdisi";
import { DosyaKaynakDugmeleri } from "../Common/DosyaKaynakSecici";
import {
  BELGE_VARYANTLARI, addPaylasimliBelgeKisiler, bugunYmd, donemYaz, dosyaHatasi, getPaylasimliBelgeler, uploadKisiBelgesi,
} from "../../services/personelBelgeService";
import { useTranslation } from "react-i18next";

const trTarih = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "—");

function VaryantKarti({ baslik, aciklama, rozet, onClick }) {
  return (
    <button
      type="button" onClick={onClick}
      className="btn btn-outline-primary text-start w-100 mb-2 p-3 d-flex flex-column gap-1"
    >
      <span className="fw-semibold d-flex align-items-center gap-2">{baslik}{rozet}</span>
      <span className="small text-body-secondary" style={{ whiteSpace: "normal" }}>{aciklama}</span>
    </button>
  );
}

// `staff` (#297): personel sayfası. Firma girişinde paylaşımlı yükleme penceresi tarih sormaz (firma tarihsiz yükler, personel onaylarken girer)
// ve tek kişilik yüklemenin sonuç mesajı belgenin personel onayını beklediğini söyler.
export default function BelgeVaryantModal({ slot, personId, personAd, firmaId, fuarId, staff = false, onClose, onDone }) {
  const { t } = useTranslation("components");
  const v = BELGE_VARYANTLARI[slot];
  const [adim, setAdim] = useState("sec"); // sec | tek | paylasimli
  const [file, setFile] = useState(null);
  const [bitis, setBitis] = useState(""); // #744: tek kişilik belgenin geçerlilik tarihi (yalnızca personel; boşsa backend bugün + 60 gün yazar)
  const [mevcutlar, setMevcutlar] = useState(null);
  const [hedefId, setHedefId] = useState(null);
  const [yeniBelge, setYeniBelge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (adim !== "paylasimli" || mevcutlar !== null) return undefined;
    let ignore = false;
    async function run() {
      try {
        const rows = await getPaylasimliBelgeler(firmaId, { aktif: 1, fileTypeId: v.paylasimli.fileTypeId });
        if (!ignore) setMevcutlar(rows);
      } catch (e) {
        if (!ignore) { setError(e.message); setMevcutlar([]); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [adim, mevcutlar, firmaId, v.paylasimli.fileTypeId]);

  if (!v) return null;
  const dosyaSorunu = file ? dosyaHatasi(file) : null;
  const bitisGecmis = staff && bitis && bitis < bugunYmd();

  async function tekYukle() {
    setBusy(true);
    setError(null);
    try {
      // WAS (until #744): uploadKisiBelgesi(personId, v.tek.fileTypeId, file, { fuarId }) — tarih hiç gönderilmiyordu, personelin yüklediği SGK / İSG hep bugün + 60 gün oluyordu.
      await uploadKisiBelgesi(personId, v.tek.fileTypeId, file, { fuarId, expiryDate: staff && bitis ? bitis : undefined });
      onDone(t(staff ? "belgeVaryant.yuklendi" : "belgeVaryant.yuklendiFdsBekliyor", { ad: v.tek.ad }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function mevcutaEkle() {
    setBusy(true);
    setError(null);
    try {
      const sonuc = await addPaylasimliBelgeKisiler(firmaId, hedefId, [personId]);
      onDone(sonuc.eklenen.length
        ? `${personAd} belgeye eklendi (Beklemede).`
        : `${personAd} zaten bu belgede.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Yeni paylaşımlı belge: bu çalışan seçili gelir.
  if (yeniBelge) {
    return (
      <PaylasimliBelgeYukleModal
        firmaId={firmaId}
        fileTypeId={v.paylasimli.fileTypeId}
        staff={staff}
        onceden={[personId]}
        oncekiBelgeler={(mevcutlar ?? []).filter((b) => b.aktif)}
        onClose={onClose}
        onDone={({ belge }) => onDone(t("belgeVaryant.yuklendiCalisanEklendiBeklemede", { ad: v.paylasimli.ad, v: belge?.kisiSayisi ?? 1 }))}
      />
    );
  }

  return (
    <Modal isOpen toggle={busy ? undefined : onClose} size="lg" scrollable>
      <ModalHeader toggle={busy ? undefined : onClose}>{v.slotAdi}{" "}{t("belgeVaryant.belgesiYukle")}{" "}{personAd}</ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color="danger" className="py-2 small">{error}</Alert>}

        {adim === "sec" && (
          <>
            <div className="small text-muted mb-2">{t("belgeVaryant.hangiBelgeyiYukluyorsunuz")}</div>
            <VaryantKarti
              baslik={v.tek.ad}
              aciklama={t("belgeVaryant.yalnizcaIcinTekBirKisiye", { personAd })}
              onClick={() => setAdim("tek")}
            />
            {/* Paylaşımlı belgeyi bir FİRMA sahiplenir; firma bağlamı yoksa (firmasız kişi sayfası, #292) sunulmaz. */}
            {firmaId ? (
              <VaryantKarti
                baslik={v.paylasimli.ad}
                rozet={<Badge color="info" className="fw-normal">{t("belgeVaryant.firmaBelgesi")}</Badge>}
                aciklama={t("belgeVaryant.firmaninBelgesiBircokCalisanaVerilir", { slotAdi: v.slotAdi })}
                onClick={() => setAdim("paylasimli")}
              />
            ) : (
              <div className="small text-muted">{v.paylasimli.ad}{" "}{t("belgeVaryant.firmaBelgesiIcinKisiSayfasinda")}</div>
            )}
          </>
        )}

        {adim === "tek" && (
          <>
            <div className="fw-semibold mb-1">{v.tek.ad}</div>
            <div className="small text-muted mb-3">{t("belgeVaryant.yalnizcaIcinKaydedilir", { personAd })}</div>
            {/* #727: personel (denetmen) tarafı Fotoğraf çek / Dosyadan yükle sorar; firma girişi eski dosya girdisini korur. */}
            {staff ? (
              <>
                <DosyaKaynakDugmeleri accept=".pdf,.jpg,.jpeg,.png" onDosya={setFile} disabled={busy} />
                <div className={`small mt-2 ${file ? "" : "text-muted"}`}>{file ? file.name : t("common:dosyaKaynagi.dosyaSecilmedi")}</div>
              </>
            ) : (
              <Input
                type="file" bsSize="sm" accept=".pdf,.jpg,.jpeg,.png" invalid={!!dosyaSorunu}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            )}
            {dosyaSorunu && <div className="small text-danger mt-1">{dosyaSorunu}</div>}
            {/* Firma girişinde tarih sorulmaz (#297): firma tarihsiz yükler, personel onaylarken girer. */}
            {staff && (
              <>
                <Label className="small mt-3 mb-1">{t("belgeVaryant.gecerlilikTarihiIstegeBagli")}</Label>
                <TarihGirdisi min={bugunYmd()} value={bitis} invalid={!!bitisGecmis} onChange={setBitis} />
                {bitisGecmis && <div className="small text-danger mt-1">{t("belgeVaryant.tarihGecmisteOlamaz")}</div>}
              </>
            )}
          </>
        )}

        {adim === "paylasimli" && (
          <>
            <div className="fw-semibold mb-1">{v.paylasimli.ad}</div>
            <div className="small text-muted mb-3">{t("belgeVaryant.firmaninBelgesiListeyeEklenirVe", { personAd })}</div>
            <Button color="primary" className="mb-3" onClick={() => setYeniBelge(true)}>
              {t("belgeVaryant.yeniBelgeYukleSecili", { personAd })}
            </Button>
            <div className="fw-semibold small mb-2">{t("belgeVaryant.veyaFirmaninMevcutBelgesineEkle")}</div>
            {mevcutlar === null ? (
              <div className="text-center py-3"><Spinner size="sm" /></div>
            ) : mevcutlar.length === 0 ? (
              <div className="small text-muted">{t("belgeVaryant.firmaninGuncelBelgesiYok", { ad: v.paylasimli.ad })}</div>
            ) : (
              mevcutlar.map((b) => {
                const zaten = b.kisiler.some((k) => Number(k.personId) === Number(personId));
                return (
                  <label
                    key={b.belgeId}
                    className={`d-flex align-items-center gap-2 border rounded p-2 mb-2 ${zaten ? "opacity-50" : ""}`}
                    style={{ cursor: zaten ? "default" : "pointer" }}
                  >
                    <Input type="radio" name="mevcut-belge" disabled={zaten} checked={hedefId === b.belgeId} onChange={() => setHedefId(b.belgeId)} />
                    <div className="me-auto">
                      <div className="fw-medium">{b.fileGivenName || b.belgeTuru}</div>
                      <div className="small text-muted">
                        {t("belgeVaryant.sonGecerlilikCalisan", { trTarih: trTarih(b.expiryDate), kisiSayisi: b.kisiSayisi })}
                        {b.belgeDonemi && <span>{" · "}{t("belgeVaryant.donem", { v: donemYaz(b.belgeDonemi) })}</span>}
                        {b.expired && <Badge color="danger" className="ms-2 fw-normal">{t("belgeVaryant.suresiDoldu")}</Badge>}
                      </div>
                    </div>
                    {zaten && <span className="small text-muted">{t("belgeVaryant.zatenBuBelgede")}</span>}
                  </label>
                );
              })
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {adim !== "sec" && (
          <Button color="light" className="me-auto" onClick={() => { setAdim("sec"); setError(null); }} disabled={busy}>{t("belgeVaryant.geri")}</Button>
        )}
        <Button color="light" onClick={onClose} disabled={busy}>{t("belgeVaryant.vazgec")}</Button>
        {adim === "tek" && (
          <Button color="primary" onClick={tekYukle} disabled={busy || !file || !!dosyaSorunu || !!bitisGecmis}>
            {busy ? <Spinner size="sm" /> : t("common:yukle")}
          </Button>
        )}
        {adim === "paylasimli" && (
          <Button color="primary" onClick={mevcutaEkle} disabled={busy || hedefId == null}>
            {busy ? <Spinner size="sm" /> : t("belgeVaryant.mevcutBelgeyeEkle")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
